import { Chip } from "@legal-500/legal500-design-system";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  platformService,
  type NotificationServiceCapabilities,
  type NotificationStreamCapability,
  type NotificationActionCapability,
  type NotificationSubscriberEntry,
} from "../../../api/platform.service.js";
import { ROUTES } from "../../../routes.js";
import NotificationEventLog from "./NotificationEventLog.js";
import styles from "./Notifications.module.css";

type StreamConsumerMap = Map<string, string[]>;

function buildStreamConsumerMap(subscribers: NotificationSubscriberEntry[]): StreamConsumerMap {
  const map: StreamConsumerMap = new Map();
  for (const subscriber of subscribers) {
    for (const subscription of subscriber.subscriptions) {
      const existing = map.get(subscription.stream);
      if (existing) {
        if (!existing.includes(subscriber.service)) {
          existing.push(subscriber.service);
        }
      } else {
        map.set(subscription.stream, [subscriber.service]);
      }
    }
  }
  return map;
}

function NotificationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab =
    location.pathname === ROUTES.developer.notificationsEventLog ? "event-log" : "capabilities";

  const { data, loading, error, refetch } = useQuery(
    () => platformService.getNotificationCapabilities(),
    [],
  );

  const streamConsumers = useMemo(
    () => buildStreamConsumerMap(data?.subscribers ?? []),
    [data?.subscribers],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Notifications" subtitle="Notification capabilities and event log" />
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "capabilities" ? styles.tabActive : ""}`}
          onClick={() => navigate(ROUTES.developer.notificationsCapabilities)}
        >
          Capabilities
        </button>
        <button
          className={`${styles.tab} ${activeTab === "event-log" ? styles.tabActive : ""}`}
          onClick={() => navigate(ROUTES.developer.notificationsEventLog)}
        >
          Event Log
        </button>
      </div>

      {activeTab === "capabilities" && (
        <>
          {loading && <LoadingState message="Loading notification capabilities..." />}

          {error && !loading && (
            <EmptyState
              variant="error"
              title="Failed to load notifications"
              message={error.message}
              action={{ label: "Retry", onClick: () => refetch() }}
            />
          )}

          {!loading && !error && (
            <div className={styles.content}>
              {!data?.services?.length ? (
                <EmptyState
                  variant="empty"
                  title="No notification capabilities discovered"
                  message="No services are currently declaring notification capabilities."
                />
              ) : (
                <div className={styles.capabilitiesTree}>
                  {data.services.map(service => (
                    <ServiceNode
                      key={service.service}
                      service={service}
                      streamConsumers={streamConsumers}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "event-log" && <NotificationEventLog />}
    </div>
  );
}

function ServiceNode({
  service,
  streamConsumers,
}: {
  service: NotificationServiceCapabilities;
  streamConsumers: StreamConsumerMap;
}) {
  return (
    <div className={styles.serviceNode}>
      <div className={styles.serviceHeader}>
        <span className={styles.serviceTitle}>{service.service}</span>
        <span className={styles.streamCount}>
          {service.streams.length} {service.streams.length === 1 ? "stream" : "streams"}
        </span>
      </div>
      <div className={styles.streamsContainer}>
        {service.streams.map(stream => (
          <StreamNode
            key={stream.stream}
            stream={stream}
            consumers={streamConsumers.get(stream.stream) ?? []}
          />
        ))}
      </div>
    </div>
  );
}

function StreamNode({
  stream,
  consumers,
}: {
  stream: NotificationStreamCapability;
  consumers: string[];
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={styles.streamNode}>
      <button
        type="button"
        className={styles.streamHeader}
        onClick={() => setExpanded(prev => !prev)}
      >
        <span className={styles.expandIcon}>{expanded ? "▾" : "▸"}</span>
        <span className={styles.streamName}>{stream.stream}</span>
        {stream.description && (
          <span className={styles.streamDescription}>{stream.description}</span>
        )}
        <span className={styles.actionCount}>
          {stream.actions.length} {stream.actions.length === 1 ? "action" : "actions"}
        </span>
      </button>
      {expanded && (
        <div className={styles.actionsContainer}>
          {stream.actions.map(action => (
            <ActionNode key={action.action} action={action} streamName={stream.stream} />
          ))}
          {consumers.length > 0 && (
            <div className={styles.consumedByRow}>
              <span className={styles.consumedByLabel}>Consumed by:</span>
              {consumers.map(consumer => (
                <span key={consumer} className={styles.consumerTag}>
                  {consumer}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionNode({
  action,
  streamName,
}: {
  action: NotificationActionCapability;
  streamName: string;
}) {
  const [schemaExpanded, setSchemaExpanded] = useState(false);
  const fullType = `${streamName}:${action.action}`;

  return (
    <div className={styles.actionNode}>
      <div className={styles.actionHeader}>
        <Chip size="small" variant="filled" color="primary">
          {action.action}
        </Chip>
        <span className={styles.actionType}>{fullType}</span>
        {action.description && (
          <span className={styles.actionDescription}>{action.description}</span>
        )}
        {action.schema && (
          <button
            type="button"
            className={styles.schemaToggle}
            onClick={() => setSchemaExpanded(prev => !prev)}
          >
            {schemaExpanded ? "Hide schema" : "Schema"}
          </button>
        )}
      </div>
      {schemaExpanded && action.schema && (
        <pre className={styles.schemaBlock}>{JSON.stringify(action.schema, null, 2)}</pre>
      )}
    </div>
  );
}

export default NotificationsPage;
